import {
    computed,
    DestroyRef,
    effect,
    inject,
    Injectable,
    InjectionToken,
    signal,
    type Provider,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserStore } from '@quipex/core/auth';
import {
    createQuery,
    normalizeToProblemDetails,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import { injectRecentBuildings } from '@quipex/feature/building-context';
import {
    BuildingsApiService,
    type BuildingListItemResponse,
} from '@quipex/shared/contracts';
import { mergeDefaultBuildingCandidates } from './merge-default-building-candidates';
import type {
    BuildingCandidate,
    BuildingCandidatesSnapshot,
    DefaultBuildingCandidates,
} from './models';

export const DEFAULT_BUILDING_CANDIDATES =
    new InjectionToken<DefaultBuildingCandidates>('DefaultBuildingCandidates');

@Injectable()
class DefaultBuildingCandidatesStore implements DefaultBuildingCandidates {
    //#region Dependency injection
    private readonly buildingsApi = inject(BuildingsApiService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly recentBuildingsStore = injectRecentBuildings();
    private readonly userStore = inject(UserStore);
    //#endregion

    //#region Query state
    private readonly portfolioBuildingsQuery = createQuery<
        readonly BuildingCandidate[],
        ResolvedProblemDetails
    >();
    //#endregion

    //#region State
    private readonly excludedIds = signal<ReadonlySet<string>>(new Set());
    private activeOrganisationId = this.userStore.org()?.id ?? null;
    private requestVersion = 0;

    public readonly snapshot = computed<BuildingCandidatesSnapshot>(() => {
        const items = mergeDefaultBuildingCandidates(
            this.recentBuildingsStore.recentBuildings(),
            this.portfolioBuildingsQuery.data() ?? [],
            this.excludedIds()
        );

        if (this.portfolioBuildingsQuery.isFetching()) {
            return { status: 'loading', items };
        }

        if (this.portfolioBuildingsQuery.isError()) {
            return { status: 'error', items };
        }

        return { status: 'ready', items };
    });
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        this.reload();
        effect(() => {
            const organisationId = this.userStore.org()?.id ?? null;
            if (organisationId === this.activeOrganisationId) return;

            this.activeOrganisationId = organisationId;
            this.requestVersion += 1;
            this.recentBuildingsStore.clear();
            this.excludedIds.set(new Set());
            this.portfolioBuildingsQuery.reset();
            this.reload();
        });
    }
    //#endregion

    //#region Public methods
    public reload(): void {
        const requestVersion = ++this.requestVersion;
        const organisationId = this.activeOrganisationId;
        const recentAtRequest = new Map(
            this.recentBuildingsStore
                .recentBuildings()
                .map((building) => [building.id, building])
        );
        this.portfolioBuildingsQuery.setLoading();

        this.buildingsApi
            .listBuildings({ page: 1, perPage: 5, sort: 'name' })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (
                        !this.isCurrentRequest(requestVersion, organisationId)
                    ) {
                        return;
                    }

                    const currentRecents = new Map(
                        this.recentBuildingsStore
                            .recentBuildings()
                            .map((building) => [building.id, building])
                    );
                    for (const item of response.items) {
                        const recent = currentRecents.get(item.id);
                        if (
                            recent &&
                            recentAtRequest.get(item.id) === recent &&
                            (recent.primaryPropertyType !==
                                item.primaryPropertyType ||
                                (item.subPremiseType !== undefined &&
                                    recent.subPremiseType !==
                                        item.subPremiseType))
                        ) {
                            this.recentBuildingsStore.refresh({
                                ...recent,
                                primaryPropertyType: item.primaryPropertyType,
                                ...(item.subPremiseType !== undefined
                                    ? { subPremiseType: item.subPremiseType }
                                    : {}),
                            });
                        }
                    }
                    this.portfolioBuildingsQuery.setData(
                        response.items.map(toBuildingCandidate)
                    );
                },
                error: (error: unknown) => {
                    if (
                        !this.isCurrentRequest(requestVersion, organisationId)
                    ) {
                        return;
                    }

                    this.portfolioBuildingsQuery.setError(
                        normalizeToProblemDetails(error)
                    );
                },
            });
    }

    public excludeDeleted(buildingId: string): void {
        this.recentBuildingsStore.remove(buildingId);
        this.excludedIds.update((excludedIds) =>
            new Set(excludedIds).add(buildingId)
        );
    }
    //#endregion

    //#region Private methods
    private isCurrentRequest(
        requestVersion: number,
        organisationId: string | null
    ): boolean {
        return (
            requestVersion === this.requestVersion &&
            organisationId === this.activeOrganisationId &&
            organisationId === (this.userStore.org()?.id ?? null)
        );
    }
    //#endregion
}

export function injectDefaultBuildingCandidates(): DefaultBuildingCandidates {
    return inject(DEFAULT_BUILDING_CANDIDATES);
}

export function provideDefaultBuildingCandidates(): Provider[] {
    return [
        {
            provide: DEFAULT_BUILDING_CANDIDATES,
            useClass: DefaultBuildingCandidatesStore,
        },
    ];
}

function toBuildingCandidate(
    building: BuildingListItemResponse
): BuildingCandidate {
    return {
        id: building.id,
        name: building.name,
        address: building.address,
        primaryPropertyType: building.primaryPropertyType,
        ...(building.subPremiseType !== undefined
            ? { subPremiseType: building.subPremiseType }
            : {}),
        ...(building.ownerOrgName
            ? { ownerOrgName: building.ownerOrgName }
            : {}),
    };
}
